import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/i18n/LocaleContext';
import type { Locale } from '@/i18n/types';
import { useEffect } from 'react';
import './ApiKeysGuidePage.css';

const SPENDING_URL = 'https://budget.florez.xyz/api/v1/spending';

const EXAMPLE_BODY = `{
  "commerceName": "Cafe Luna",
  "amount": "12.50",
  "occurredDate": "2026-07-06"
}`;

export function ApiKeysGuidePage() {
  const { isAuthenticated } = useAuth();
  const { locale, setLocale, t, guide } = useLocale();

  useEffect(() => {
    const previous = document.title;
    document.title = guide.documentTitle;
    return () => {
      document.title = previous;
    };
  }, [guide.documentTitle]);

  const switchLocale = (next: Locale) => {
    if (next !== locale) setLocale(next);
  };

  return (
    <div className="guide" lang={locale}>
      <header className="guide-top">
        <div className="guide-top__inner">
          <Link to={isAuthenticated ? '/dashboard' : '/'} className="guide-brand">
            <img src="/favicon.svg" width={28} height={28} alt="" draggable={false} />
            {t.brand}
          </Link>
          <div className="guide-top__actions">
            <div className="guide-lang" role="group" aria-label={t.languageAria}>
              <button
                type="button"
                className={`guide-lang__btn${locale === 'en' ? ' is-active' : ''}`}
                aria-pressed={locale === 'en'}
                onClick={() => switchLocale('en')}
              >
                {t.langEn}
              </button>
              <button
                type="button"
                className={`guide-lang__btn${locale === 'es' ? ' is-active' : ''}`}
                aria-pressed={locale === 'es'}
                onClick={() => switchLocale('es')}
              >
                {t.langEs}
              </button>
            </div>
            <Link to={isAuthenticated ? '/settings' : '/'} className="guide-back">
              {isAuthenticated ? guide.backSettings : guide.backHome}
            </Link>
          </div>
        </div>
      </header>

      <main className="guide-main">
        <header className="guide-hero">
          <p className="guide-label">{guide.heroLabel}</p>
          <h1>{guide.heroTitle}</h1>
          <p className="guide-lead">{guide.heroLead}</p>
        </header>

        <nav className="guide-toc" aria-label={guide.tocLabel}>
          <p className="guide-toc__label">{guide.tocLabel}</p>
          <ol>
            <li>
              <a href="#overview">{guide.tocOverview}</a>
            </li>
            <li>
              <a href="#create">{guide.tocCreate}</a>
            </li>
            <li>
              <a href="#shortcuts">{guide.tocShortcuts}</a>
            </li>
            <li>
              <a href="#faq">{guide.tocFaq}</a>
            </li>
          </ol>
        </nav>

        <section id="overview" className="guide-section" aria-labelledby="overview-heading">
          <h2 id="overview-heading">{guide.overviewTitle}</h2>
          <p>{guide.overviewBody}</p>
          <p className="guide-callout">{guide.overviewNotLogin}</p>

          <h3>{guide.permTitle}</h3>
          <ul className="guide-perm-list">
            <li>
              <code>{guide.permReadCode}</code>
              <span>{guide.permReadBody}</span>
            </li>
            <li>
              <code>{guide.permWriteCode}</code>
              <span>{guide.permWriteBody}</span>
            </li>
          </ul>

          <h3>{guide.securityTitle}</h3>
          <ul className="guide-tips">
            <li>{guide.securityTip1}</li>
            <li>{guide.securityTip2}</li>
            <li>{guide.securityTip3}</li>
            <li>{guide.securityTip4}</li>
          </ul>
        </section>

        <section id="create" className="guide-section" aria-labelledby="create-heading">
          <h2 id="create-heading">{guide.createTitle}</h2>
          <p>{guide.createLead}</p>

          <ol className="guide-steps">
            <li>
              <h3>{guide.createStep1Title}</h3>
              <p>{guide.createStep1Body}</p>
            </li>
            <li>
              <h3>{guide.createStep2Title}</h3>
              <p>{guide.createStep2Body}</p>
            </li>
            <li>
              <h3>{guide.createStep3Title}</h3>
              <p>{guide.createStep3Body}</p>
            </li>
            <li>
              <h3>{guide.createStep4Title}</h3>
              <p>{guide.createStep4Body}</p>
            </li>
          </ol>

          <figure className="guide-figure">
            <img
              src="/guide/04-create-api-key.webp"
              alt=""
              width={1536}
              height={1094}
              loading="lazy"
            />
            <figcaption>{guide.createFigureCaption}</figcaption>
          </figure>

          {isAuthenticated && (
            <p>
              <Link to="/settings" className="guide-cta">
                {guide.openSettingsCta}
              </Link>
            </p>
          )}
        </section>

        <section id="shortcuts" className="guide-section" aria-labelledby="shortcuts-heading">
          <h2 id="shortcuts-heading">{guide.shortcutsTitle}</h2>
          <p>{guide.shortcutsLead}</p>

          <div className="guide-endpoint">
            <span className="guide-endpoint__label">{guide.shortcutsEndpointLabel}</span>
            <code>POST {SPENDING_URL}</code>
          </div>

          <h3>{guide.shortcutsFieldsTitle}</h3>
          <ul className="guide-fields">
            <li>
              <code>{guide.fieldCommerce}</code>
              <span>{guide.fieldCommerceHint}</span>
            </li>
            <li>
              <code>{guide.fieldAmount}</code>
              <span>{guide.fieldAmountHint}</span>
            </li>
            <li>
              <code>{guide.fieldDate}</code>
              <span>{guide.fieldDateHint}</span>
            </li>
          </ul>
          <ul className="guide-tips">
            <li>{guide.responseAssigned}</li>
            <li>{guide.responseUnassigned}</li>
          </ul>

          <ol className="guide-steps">
            <li>
              <h3>{guide.shortcutStep1Title}</h3>
              <p>{guide.shortcutStep1Body}</p>
              <figure className="guide-figure">
                <img
                  src="/guide/01-shortcuts-automation.webp"
                  alt=""
                  width={1536}
                  height={1094}
                  loading="lazy"
                />
                <figcaption>{guide.shortcutStep1Caption}</figcaption>
              </figure>
            </li>
            <li>
              <h3>{guide.shortcutStep2Title}</h3>
              <p>{guide.shortcutStep2Body}</p>
            </li>
            <li>
              <h3>{guide.shortcutStep3Title}</h3>
              <p>{guide.shortcutStep3Body}</p>
              <figure className="guide-figure">
                <img
                  src="/guide/02-shortcuts-request.webp"
                  alt=""
                  width={1536}
                  height={1094}
                  loading="lazy"
                />
                <figcaption>{guide.shortcutStep3Caption}</figcaption>
              </figure>
            </li>
            <li>
              <h3>{guide.shortcutStep4Title}</h3>
              <p>{guide.shortcutStep4Body}</p>
              <figure className="guide-figure">
                <img
                  src="/guide/03-shortcuts-json-body.webp"
                  alt=""
                  width={1536}
                  height={1094}
                  loading="lazy"
                />
                <figcaption>{guide.shortcutStep4Caption}</figcaption>
              </figure>
              <p className="guide-code-label">{guide.exampleJsonLabel}</p>
              <pre className="guide-code">
                <code>{EXAMPLE_BODY}</code>
              </pre>
            </li>
            <li>
              <h3>{guide.shortcutStep5Title}</h3>
              <p>{guide.shortcutStep5Body}</p>
            </li>
            <li>
              <h3>{guide.shortcutStep6Title}</h3>
              <p>{guide.shortcutStep6Body}</p>
            </li>
          </ol>
        </section>

        <section id="faq" className="guide-section guide-section--faq" aria-labelledby="faq-heading">
          <h2 id="faq-heading">{guide.faqTitle}</h2>
          <div className="guide-faq">
            <details>
              <summary>{guide.faq1Q}</summary>
              <p>{guide.faq1A}</p>
            </details>
            <details>
              <summary>{guide.faq2Q}</summary>
              <p>{guide.faq2A}</p>
            </details>
            <details>
              <summary>{guide.faq3Q}</summary>
              <p>{guide.faq3A}</p>
            </details>
            <details>
              <summary>{guide.faq4Q}</summary>
              <p>{guide.faq4A}</p>
            </details>
            <details>
              <summary>{guide.faq5Q}</summary>
              <p>{guide.faq5A}</p>
            </details>
            <details>
              <summary>{guide.faq6Q}</summary>
              <p>{guide.faq6A}</p>
            </details>
            <details>
              <summary>{guide.faq7Q}</summary>
              <p>{guide.faq7A}</p>
            </details>
            <details>
              <summary>{guide.faq8Q}</summary>
              <p>{guide.faq8A}</p>
            </details>
          </div>
        </section>
      </main>

      <footer className="guide-footer">
        <p>{guide.footerNote}</p>
      </footer>
    </div>
  );
}
